import { httpClient } from "@/lib/httpClient";

/** One open Accelo task the user can log time against. */
export interface AcceloTask {
  /** Accelo task id, kept exactly as Accelo gives it. */
  id: string;
  /** Task title, shown as the option text. */
  title: string;
}

/** The open tasks of one Accelo project, grouped under its client. */
export interface AcceloTaskGroup {
  /** Client name, shown as the optgroup label. */
  client: string;
  /** Accelo project id, also used as the React key. */
  projectId: string;
  /** Accelo project title. */
  project: string;
  tasks: readonly AcceloTask[];
}

/**
 * The two 200 answers of `GET /api/timesheet/tasks`. A failed Accelo read is a
 * 502, which the http client throws as an `ApiError`.
 */
export type AcceloTasksPayload =
  | { status: "ok"; groups: readonly AcceloTaskGroup[] }
  | {
      /** The signed-in email did not resolve to exactly one Accelo staff member. */
      status: "refused";
      reason: "no-match" | "ambiguous";
      /** The plain sentence the page shows. The route owns the wording. */
      message: string;
      /** Always empty. */
      groups: readonly [];
    };

/** One row as `POST /api/timesheet/publish` takes it. */
export interface PublishRowInput {
  rowId: string;
  name: string;
  taskId: string;
  elapsedMs: number;
  /**
   * The epoch second of midday on the row's own day, so the entry lands on
   * that day. Absent when the row's day does not read as a real day. The
   * route skips such a row.
   */
  dateSeconds?: number;
}

export type PublishRowOutcome = "published" | "already-published" | "skipped" | "failed";

/** What the route says happened to one row. */
export interface PublishRowResult {
  rowId: string;
  outcome: PublishRowOutcome;
  /** The Accelo activity. Present when published, and on a failed read-back. */
  activityId?: string;
  /** The seconds Accelo reports storing for this activity. */
  storedSeconds?: number;
  /** The route's plain sentence. Present whenever the outcome is not `published`. */
  message?: string;
}

/**
 * The 200 answers of `POST /api/timesheet/publish`. A malformed body is a 400
 * and a publish that sent nothing is a 502; the http client throws both as an
 * `ApiError` whose `details` carry the route's `message`.
 */
export type PublishPayload =
  /** `ok`: no row failed. `incomplete`: at least one row failed and can be retried. */
  | { status: "ok" | "incomplete"; rows: readonly PublishRowResult[] }
  | {
      /** The signed-in email did not resolve to exactly one Accelo staff member. */
      status: "refused";
      reason: "no-match" | "ambiguous";
      /** The plain sentence the page shows. The route owns the wording. */
      message: string;
      /** Always empty: a refusal sends nothing. */
      rows: readonly [];
    };

/** One Teams meeting of the asked day, from the signed-in person's own calendar. */
export interface TeamsMeeting {
  /** The Graph event id. */
  id: string;
  /** The meeting subject, or "Teams meeting" when it has none. */
  subject: string;
  /** Scheduled start, epoch millisecond. */
  startMs: number;
  /** Scheduled end, epoch millisecond. */
  endMs: number;
  /** Scheduled length, `endMs - startMs`. */
  durationMs: number;
}

/**
 * The 200 answers of `GET /api/timesheet/meetings`. `needs-sign-in` means the
 * session has no calendar consent yet. A failed Graph read is a 502, which the
 * http client throws as an `ApiError`.
 */
export type TeamsMeetingsPayload =
  | { status: "ok"; meetings: readonly TeamsMeeting[] }
  | { status: "needs-sign-in"; meetings: readonly [] };

/**
 * Timesheet API. The Accelo task list is served behind the sign-in gate, so
 * none of it ships in the client bundle.
 */
export const timesheetService = {
  getTasks: () => httpClient.get<AcceloTasksPayload>("/api/timesheet/tasks"),

  /** The Teams meetings of `date` (`YYYY-MM-DD`) in the IANA zone `tz`. */
  getMeetings: (date: string, tz: string) =>
    httpClient.get<TeamsMeetingsPayload>(
      `/api/timesheet/meetings?${new URLSearchParams({ date, tz }).toString()}`
    ),

  /** Send rows to Accelo as the signed-in person's own logged time. */
  publish: (rows: readonly PublishRowInput[], csrfToken: string, signal?: AbortSignal) =>
    httpClient.post<PublishPayload>("/api/timesheet/publish", {
      json: { rows },
      headers: { "x-csrf-token": csrfToken },
      signal,
      timeoutMs: 150_000,
    }),
};
